import { useEffect, useState } from "react";
import { toast } from "sonner";

import CenterManagerFeedbackPanel from "@/components/CenterManagerFeedbackPanel";
import CenterManagerLearnerAudit from "@/components/CenterManagerLearnerAudit";
import CenterManagerLearnerStatus from "@/components/CenterManagerLearnerStatus";
import CenterManagerLearnerTable from "@/components/CenterManagerLearnerTable";
import CenterManagerOverview from "@/components/CenterManagerOverview";
import CenterManagerTrainerList from "@/components/CenterManagerTrainerList";
import { LoadingState } from "@/components/DashboardParts";
import RoleLayout from "@/components/RoleLayout";
import TrainerAttendanceReport from "@/components/TrainerAttendanceReport";
import { api, formatApiError } from "@/lib/api";

export default function CenterManagerWorkspace() {
  const [data, setData] = useState(null);
  const [editingLearner, setEditingLearner] = useState(null);
  const [auditVersion, setAuditVersion] = useState(0);

  const refresh = () => api
    .get("/lms/center-manager/dashboard")
    .then((response) => setData(response.data));

  useEffect(() => {
    refresh();
  }, []);

  if (!data) {
    return <LoadingState label="Loading center workspace" />;
  }

  const action = async (path, message) => {
    try {
      await api.post(path);
      toast.success(message);
      await refresh();
      setAuditVersion((value) => value + 1);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const saveLearner = async (event) => {
    event.preventDefault();
    try {
      await api.put(`/lms/center-manager/learners/${editingLearner.id}`, editingLearner);
      toast.success("Learner updated");
      setEditingLearner(null);
      await refresh();
      setAuditVersion((value) => value + 1);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <RoleLayout eyebrow={data.center_name || "Training Center"} title="Center Manager">
      <CenterManagerOverview
        trainers={data.trainers}
        batches={data.batches}
        learners={data.learners}
      />
      <CenterManagerTrainerList trainers={data.trainers} />
      {editingLearner && (
        <form
          className="editor-form-grid"
          onSubmit={saveLearner}
          data-testid="center-manager-edit-learner-form"
        >
          <label>
            Name
            <input
              value={editingLearner.name}
              onChange={(event) => setEditingLearner({
                ...editingLearner,
                name: event.target.value,
              })}
              data-testid="center-manager-edit-learner-name-input"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={editingLearner.email}
              onChange={(event) => setEditingLearner({
                ...editingLearner,
                email: event.target.value,
              })}
              data-testid="center-manager-edit-learner-email-input"
            />
          </label>
          <label>
            Batch
            <select
              value={editingLearner.batch_id}
              onChange={(event) => setEditingLearner({
                ...editingLearner,
                batch_id: event.target.value,
              })}
              data-testid="center-manager-edit-learner-batch-select"
            >
              {data.batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </label>
          <button className="primary-button" data-testid="center-manager-save-learner-button">
            Save learner
          </button>
        </form>
      )}
      <CenterManagerLearnerTable
        learners={data.learners}
        batches={data.batches}
        onEdit={setEditingLearner}
        onRemoveBatch={(id) => action(
          `/lms/center-manager/learners/${id}/remove-from-batch`,
          "Learner removed from batch",
        )}
        onDeactivate={(id) => action(
          `/lms/center-manager/learners/${id}/deactivate`,
          "Learner deactivated",
        )}
        onReactivate={(id) => action(
          `/lms/center-manager/learners/${id}/reactivate`,
          "Learner reactivated",
        )}
      />
      <CenterManagerLearnerStatus learners={data.learners} />
      <CenterManagerLearnerAudit refreshKey={auditVersion} />
      <CenterManagerFeedbackPanel trainers={data.trainers} />
      <TrainerAttendanceReport fixedCenterId={data.center_id} />
    </RoleLayout>
  );
}